import { CATALOGICAL_ENDPOINT } from "config";
import { StatusCodes } from "http-status-codes";
import {
  AddItemToListRequest,
  CreateListRequest,
  EvaluateListsRequest,
  EvaluateListsResponse,
  GetListResponse,
  GetListsResponse,
  UpdateListRequest,
  Visibility,
} from "lib/models/list-models";

import { BaseService } from "./base-service";

export class ListsService extends BaseService {
  constructor() {
    super(CATALOGICAL_ENDPOINT + "/cat-lists");
  }

  public async getLists(
    options: {
      includeItemDetails?: boolean;
      visibility?: Visibility;
      tags?: string[];
      allowedDomains?: string[];
      hasRules?: boolean;
      owners?: string;
    },
    itemOffset: number = 0,
    itemLimit: number = 100
  ): Promise<GetListsResponse> {
    const queryParams = new URLSearchParams({
      itemOffset: itemOffset.toString(),
      itemLimit: itemLimit.toString(),
    });

    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((item) => queryParams.append(key, item));
        } else {
          queryParams.append(key, value.toString());
        }
      }
    });

    const url = `/lists?${queryParams.toString()}`;
    const response = await this.fetch(url);

    if (response.status === StatusCodes.OK) {
      return await response.json();
    }
    throw new Error("Failed to fetch lists");
  }

  public async createList(listData: CreateListRequest, csrfToken: string): Promise<GetListResponse> {
    const url = "/lists";
    const response = await this.fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken,
      },
      body: JSON.stringify(listData),
    });

    if (response.status === StatusCodes.CREATED) {
      return await response.json();
    }
    throw new Error("Failed to create list");
  }

  public async deleteList(listId: string, csrfToken: string): Promise<void> {
    const url = `/lists/${encodeURIComponent(listId)}`;
    const response = await this.fetch(url, {
      method: "DELETE",
      headers: {
        "X-CSRF-TOKEN": csrfToken,
      },
    });

    if (response.status !== StatusCodes.NO_CONTENT && response.status !== StatusCodes.OK) {
      throw new Error("Failed to delete list");
    }
  }

  public async getList(
    listId: string,
    includeItemDetails: boolean = true,
    itemOffset: number = 0,
    itemLimit: number = 100
  ): Promise<GetListResponse> {
    const queryParams = new URLSearchParams({
      includeItemDetails: includeItemDetails.toString(),
      itemOffset: itemOffset.toString(),
      itemLimit: itemLimit.toString(),
    });

    const url = `/lists/${encodeURIComponent(listId)}?${queryParams.toString()}`;
    const response = await this.fetch(url);

    if (response.status === StatusCodes.OK) {
      return await response.json();
    }
    throw new Error(`Failed to fetch list: ${listId}`);
  }

  public async updateList(listId: string, listData: UpdateListRequest, csrfToken: string): Promise<GetListResponse> {
    const url = `/lists/${encodeURIComponent(listId)}`;
    const response = await this.fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken,
      },
      body: JSON.stringify(listData),
    });

    if (response.status === StatusCodes.OK) {
      return await response.json();
    }
    throw new Error("Failed to update list");
  }

  public async addItemToList(request: AddItemToListRequest, csrfToken: string): Promise<GetListResponse> {
    const queryParams = new URLSearchParams();
    if (request.listId) queryParams.append("listId", request.listId);
    if (request.autoCreate) queryParams.append("autoCreate", "true");
    if (request.name) queryParams.append("name", request.name);
    if (request.domain) queryParams.append("domain", request.domain);

    const url = `/lists/items?${queryParams.toString()}`;
    const response = await this.fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken,
      },
      body: JSON.stringify({ itemIds: request.itemIds }),
    });

    if (response.status === StatusCodes.CREATED || response.status === StatusCodes.OK) {
      return await response.json();
    }
    throw new Error("Failed to add items to list");
  }

  public async removeItemFromList(listId: string, itemId: string, csrfToken: string): Promise<void> {
    const queryParams = new URLSearchParams({
      itemId: itemId,
    });
    const url = `/lists/items/${encodeURIComponent(listId)}?${queryParams.toString()}`;
    const response = await this.fetch(url, {
      method: "DELETE",
      headers: {
        "X-CSRF-TOKEN": csrfToken,
      },
    });

    if (response.status !== StatusCodes.NO_CONTENT && response.status !== StatusCodes.OK) {
      throw new Error("Failed to remove item from list");
    }
  }

  public async evaluateLists(
    attributes: Record<string, any>,
    options: { domains?: string[]; includeItemDetails?: boolean; limit?: number } = {},
    csrfToken: string
  ): Promise<EvaluateListsResponse> {
    const queryParams = new URLSearchParams();
    if (options.includeItemDetails !== undefined) {
      queryParams.append("includeItemDetails", options.includeItemDetails.toString());
    }

    const url = `/lists/evaluate?${queryParams.toString()}`;
    const response = await this.fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken,
      },
      body: JSON.stringify({ attributes, domains: options.domains, limit: options.limit }),
    });

    if (response.status === StatusCodes.OK) {
      return await response.json();
    }
    throw new Error("Failed to evaluate lists");
  }
}
